<template>
  <section v-if="levelIntervals.length > 0" class="books-class-filter">
    <h3>
      <FaIcon icon="sliders-h"></FaIcon> {{ $t('books-filter.choose-level') }}
    </h3>

    <ul class="filters">
      <li
        class="filter"
        v-for="interval in levelIntervals"
        :key="interval.id"
        :class="[
          { selected: selectedLevelIntervalId === interval.id },
          'interval-' + interval.id,
        ]"
      >
        <label class="filter-input-label" :for="'interval-' + interval.id">
          <CdnImage
            class="filter-book-icon"
            :image-path="getFilterIconPath(interval.id)"
          ></CdnImage>
          <span class="interval-name">{{ getFilterName(interval) }}</span>
        </label>

        <input
          type="radio"
          :id="'interval-' + interval.id"
          class="filter-radio"
          name="filterIntervalSelection"
          :value="interval.id"
          v-model="selectedLevelIntervalId"
        />
      </li>
    </ul>
  </section>
</template>

<script>
import FaIcon from '../ui/FaIcon'
import CdnImage from '../ui/CdnImage'

/**
 * @typedef {Object<{
 *  active: string,
 * 	all_span: string,
 * 	id: string,
 * 	max_level_id: string,
 * 	min_level_id: string,
 * 	name: string,
 * }>} levelIntervalData
 */

/**
 * @type {[string]}
 */
const booksFilterIconTypes = [
  'orange',
  'red',
  'lightblue',
  'darkblue',
  'green',
  'tealblue',
  'all',
]

export default {
  name: 'booksClassFilter',
  components: { CdnImage, FaIcon },
  props: {
    levelIntervals: Array,
    userLevel: Number,
    isTeacher: Boolean,
  },
  data() {
    return {
      selectedLevelIntervalId: undefined,
    }
  },
  mounted() {
    this.setStartingLevelFilterCategory(this.levelIntervals)
  },
  computed: {
    /**
     * The raw selected class interval data.
     * @returns {levelIntervalData}
     */
    selectedIntervalData() {
      if (!this.levelIntervals) {
        return null
      }

      let id = this.selectedLevelIntervalId

      return this.levelIntervals.find(
        function(interval) {
          return interval.id === id
        }.bind(this)
      )
    },
    /**
     * The minimum level set by class filter
     * @returns {number}
     */
    showMinClass() {
      return parseInt(
        this.selectedIntervalData
          ? this.selectedIntervalData.min_level_id
          : this.$config.classMinLevel,
        10
      )
    },
    /**
     * The maximum level set by class filter
     * @returns {number}
     */
    showMaxClass() {
      return parseInt(
        this.selectedIntervalData
          ? this.selectedIntervalData.max_level_id
          : this.$config.classXMaxLevel,
        10
      )
    },
  },
  methods: {
    /**
     * Gets the url path for a filter icon
     *
     * @param {number|string} intervalId
     * @returns {string}
     */
    getFilterIconPath(intervalId) {
      return (
        '/fessor/bookshelf/bookicons/books_icon_' +
        booksFilterIconTypes[intervalId - 1] +
        '.svg'
      )
    },
    /**
     * Gets the name for a filter.
     * We'd like to use a custom name for the interval that contains all levels,
     * hence this function.
     *
     * @param {levelIntervalData} intervalData
     * @returns {string}
     */
    getFilterName(intervalData) {
      return intervalData.all_span
        ? this.$t('books-filter.all-books')
        : intervalData.name
    },
    /**
     * Finds and sets the starting filter category depending on the level set on the user.
     * If the user is logged out or a teacher, the interval will always be set to "all"
     *
     * @param {Array<levelIntervalData>} levelIntervals
     */
    setStartingLevelFilterCategory(levelIntervals) {
      if (
        !this.selectedLevelIntervalId &&
        Array.isArray(levelIntervals) &&
        levelIntervals.length > 0
      ) {
        let intervalWithLargestSpan = null

        if (this.isTeacher || (!this.userLevel && this.userLevel !== 0)) {
          intervalWithLargestSpan = levelIntervals.find(function(interval) {
            return interval['all_span'] === true
          })

          this.selectedLevelIntervalId = intervalWithLargestSpan.id
        } else {
          // Find the All_span interval index,
          // since we want this to be our default
          // setting if none of the other intervals fit.
          let selectedInterval = levelIntervals.find(function(interval) {
            return interval['all_span']
          })

          // We are looking for intervals that are even more specific according to the user level
          levelIntervals.forEach(
            function(interval) {
              if (
                this.islevelWithinInterval(this.userLevel, interval) &&
                this.isIntervalMoreSpecific(interval, selectedInterval)
              ) {
                selectedInterval = interval
              }
            }.bind(this)
          )

          this.selectedLevelIntervalId = selectedInterval.id
        }
      }
    },
    /**
     * @param {number} level
     * @param {levelIntervalData} interval
     * @return {boolean}
     */
    islevelWithinInterval(level, interval) {
      return level >= interval.min_level_id && level <= interval.max_level_id
    },
    /**
     * @param {levelIntervalData} interval
     * @param {levelIntervalData} previousInterval
     * @return{boolean}
     */
    isIntervalMoreSpecific(interval, previousInterval) {
      return (
        interval.max_level_id - interval.min_level_id <
        previousInterval.max_level_id - previousInterval.min_level_id
      )
    },
  },
  watch: {
    /**
     * @param {Array<levelIntervalData>} levelIntervals
     */
    levelIntervals(levelIntervals) {
      this.setStartingLevelFilterCategory(levelIntervals)
    },
    /**
     * emits/dispatches an event whenever the user has changed the selected interval to show
     *
     * @param {levelIntervalData} intervalData
     */
    selectedIntervalData(intervalData) {
      if (intervalData) {
        this.$emit(
          'classIntervalChanged',
          intervalData.min_level_id,
          intervalData.max_level_id,
          this.getFilterName(intervalData)
        )
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.filters {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(12.5rem, 1fr));
  grid-gap: 2em 3em;
  align-items: end;
  padding: 0;

  list-style: none;
  box-sizing: border-box;

  .filter {
    --interval-color: orange;

    padding: 0;
    box-sizing: border-box;
    background: none; // Removes the default Fessor list bullet style
    opacity: 0.5;
    transition: opacity 0.2s ease-in;

    > .filter-input-label {
      vertical-align: middle;
      box-sizing: border-box;
    }

    > .filter-input-label {
      display: flex;
      flex-flow: row nowrap;
      align-items: flex-end;
      cursor: pointer;

      > .interval-name {
        flex: 1;
        font-size: 1.2em;
      }
    }

    .filter-book-icon {
      display: inline-block;
      width: 3.5em;
      margin-right: 0.7em;
      vertical-align: bottom;
    }

    &:after {
      display: block;
      height: 0.3em;
      width: 100%;
      margin-top: 0.3em;
      content: '';
      background-color: var(--interval-color);
      border-radius: 1em;
    }

    .filter-radio {
      display: none;
    }

    &.selected,
    &:hover {
      opacity: 1;
    }
    &.interval-1 {
      --interval-color: #fe8f1d;
    }
    &.interval-2 {
      --interval-color: #f23a3a;
    }
    &.interval-3 {
      --interval-color: #0575a4;
    }
    &.interval-4 {
      --interval-color: #16445a;
    }
    &.interval-5 {
      --interval-color: #62c979;
    }
    &.interval-6 {
      --interval-color: #43a8a2;
    }
    &.interval-7 {
      --interval-color: #b7906e;
    }
  }
}
</style>
